<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import BuzzScoreSparkline from './BuzzScoreSparkline.vue';
import BuzzScoreDelta from './BuzzScoreDelta.vue';
import { scoreColorClass } from './buzzScoreDistribution';

const props = defineProps({
  score: {
    type: [Number, String],
    default: null,
  },
  avg28d: {
    type: [Number, String],
    default: null,
  },
  periodDelta: {
    type: [Number, String],
    default: null,
  },
  history: {
    type: Array,
    default: () => [],
  },
  title: {
    type: String,
    default: '',
  },
  avgLabelKey: {
    type: String,
    default: 'OVERVIEW_REPORTS.BUZZ_SCORES.AVG_28D',
  },
  noScoreLabelKey: {
    type: String,
    default: 'OVERVIEW_REPORTS.BUZZ_SCORES.NO_SCORE',
  },
  compact: {
    type: Boolean,
    default: false,
  },
});

const BUZZ_SCORE_ICON = 'i-ph-hands-clapping';

const { t } = useI18n();

const trendValues = computed(() =>
  (props.history || [])
    .filter(point => point?.scored_at != null && point?.b != null)
    .map(point => Number(point.b))
);

const avgLabel = computed(() => {
  if (props.avg28d != null && props.avg28d !== '') {
    return t(props.avgLabelKey, { avg: props.avg28d });
  }
  return t(props.noScoreLabelKey);
});
const CARD_SHELL_CLASS =
  'm-0.5 rounded-xl shadow outline-1 outline outline-n-container bg-n-solid-2 overflow-hidden';
</script>

<template>
  <div
    class="relative w-full"
    :class="[
      compact ? CARD_SHELL_CLASS : 'rounded-xl overflow-hidden bg-n-alpha-2',
      compact ? 'min-h-0' : 'min-h-40',
    ]"
  >
    <BuzzScoreSparkline
      :values="trendValues"
      :avg-value="avg28d"
      edge-to-edge
      :compact="compact"
      sparkline-class="text-n-slate-9"
    />

    <div
      class="relative z-10 flex items-start justify-between gap-3"
      :class="compact ? 'px-4 pt-3 pb-16' : 'px-5 pt-4 pb-5 gap-4'"
    >
      <div class="flex flex-col gap-0.5 min-w-0">
        <p
          v-if="title"
          class="font-medium text-n-slate-12 truncate mb-0"
          :class="compact ? 'text-xs' : 'text-sm'"
        >
          {{ title }}
        </p>
        <div class="flex items-end gap-2">
          <p
            class="font-semibold leading-none mb-0 tabular-nums"
            :class="[scoreColorClass(score), compact ? 'text-6xl' : 'text-5xl']"
          >
            {{ score ?? '—' }}
          </p>
          <Icon
            :icon="BUZZ_SCORE_ICON"
            class="shrink-0 mb-0.5"
            :class="[scoreColorClass(score), compact ? 'size-9' : 'size-8']"
          />
          <BuzzScoreDelta
            :period-delta="periodDelta"
            :history="history"
            :score="score"
          />
        </div>
      </div>
      <p class="text-xs text-n-slate-10 mb-0 shrink-0">
        {{ avgLabel }}
      </p>
    </div>
  </div>
</template>
